// components/BatchProcessing/BatchProcessingStatus.jsx
import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

const BatchProcessingStatus = ({ batchStatus, totalItems, onCancel }) => {
  const getFileNameFromPath = (path) => {
    if (!path) return 'desconhecido';
    return path.split('/').pop() || path.split('\\').pop() || path;
  };

  const parseErrorMessage = (errorMsg) => {
    if (!errorMsg) return { display: errorMsg, hasTaskInfo: false };
    
    if (errorMsg.includes('Tarefa') && errorMsg.includes(':')) {
      return {
        display: errorMsg,
        hasTaskInfo: true
      };
    }
    
    return { display: errorMsg, hasTaskInfo: false };
  };

  const sortedTasks = React.useMemo(() => {
    if (!batchStatus?.tasks_status || !Array.isArray(batchStatus.tasks_status)) return [];
    
    return [...batchStatus.tasks_status].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateA - dateB;
    });
  }, [batchStatus?.tasks_status]);

  const getTaskDisplayName = (task, index) => {
    if (task.task_name && task.task_name.trim() !== '') {
      return `${task.task_name}`;
    }
    if (task.axis_file) {
      const fileName = getFileNameFromPath(task.axis_file);
      return `Tarefa ${index + 1}: ${fileName}`;
    }
    return `Tarefa ${index + 1}`;
  };

  const tasksWithClearErrors = React.useMemo(() => {
    return sortedTasks.filter(task => {
      const errorInfo = parseErrorMessage(task.error_message);
      return task.status === 'FAILED' && errorInfo.hasTaskInfo;
    }).length;
  }, [sortedTasks]);

  if (!batchStatus) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Iniciando processamento em lote...
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Preparando {totalItems} tarefas para processamento
        </Typography>
      </Box>
    );
  }

  const { tasks_status, progress, total_tasks, completed_tasks, failed_tasks } = batchStatus;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED': return <CheckIcon color="success" />;
      case 'FAILED': return <ErrorIcon color="error" />;
      case 'PROCESSING': return <PlayIcon color="primary" />;
      default: return <ScheduleIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'FAILED': return 'error';
      case 'PROCESSING': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Processamento em Andamento
      </Typography>

      {/* Alertas informativos */}
      {tasksWithClearErrors > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tasksWithClearErrors} tarefa(s) mostraram erro específico. As mensagens de erro incluem identificação da tarefa.
        </Alert>
      )}

      {/* Progresso Geral */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" gutterBottom>
                Progresso do Lote
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {progress}%
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-around' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    {completed_tasks || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Concluídos
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="error">
                    {failed_tasks || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Falhas
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6">
                    {total_tasks || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lista de Tarefas Individuais */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Tarefas Individuais ({sortedTasks.length})
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Ordenadas por ordem de criação
            </Typography>
          </Box>
          
          {sortedTasks.length > 0 ? (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="50">#</TableCell>
                    <TableCell>Identificação</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Direção</TableCell>
                    <TableCell>Progresso</TableCell>
                    <TableCell>Mensagem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTasks.map((task, displayIndex) => {
                    const errorInfo = parseErrorMessage(task.error_message);
                    const displayName = getTaskDisplayName(task, displayIndex);
                    
                    return (
                      <TableRow key={task.task_id || displayIndex}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {displayIndex + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {displayName}
                            </Typography>
                            {task.task_name && task.axis_file_name && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                Arquivo: {task.axis_file_name}
                              </Typography>
                            )}
                            {task.created_at && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                Criada: {new Date(task.created_at).toLocaleTimeString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(task.status)}
                            label={task.status}
                            color={getStatusColor(task.status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {task.eixo_direction || 'Automático'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {task.status === 'PROCESSING' && (
                            <LinearProgress 
                              variant="indeterminate" 
                              sx={{ width: 60, height: 6 }}
                            />
                          )}
                          {task.status === 'COMPLETED' && (
                            <Typography variant="caption" color="success.main">
                              100%
                            </Typography>
                          )}
                          {task.status === 'FAILED' && (
                            <Typography variant="caption" color="error.main">
                              Falhou
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="caption" 
                            color={task.error_message ? "error" : "textSecondary"}
                            sx={{ 
                              fontFamily: task.error_message ? 'monospace' : 'inherit',
                              fontSize: '0.7rem',
                              wordBreak: 'break-word'
                            }}
                          >
                            {errorInfo.display || (task.status === 'PROCESSING' ? 'Processando...' : 'Concluído')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={30} sx={{ mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Aguardando início das tarefas...
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={onCancel}
            >
              Cancelar Processamento
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BatchProcessingStatus;